import { type ReactNode, useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Clock, FileSearch, LayoutDashboard, LogOut, Moon, Settings, Sun, Terminal } from 'lucide-react';
import clsx from 'clsx';
import type { Permission, SessionUser } from '@idas/shared';
import { api } from '../lib/api';
import { useConsoleStore } from '../store/console.store';
import { ToastViewport } from './ui/Toast';
import { IntervalLogo, IntervalLoader } from './ui/IntervalLogo';
import { Avatar } from './ui/Avatar';

/**
 * Nav entries are gated on a capability, not a role name.
 *
 * An auditor sees Audit but not Console — they hold `audit:read` and
 * deliberately not `query:run`, because the person reviewing access should not
 * also hold it. Keying off permissions rather than roles means that stays true
 * automatically if the role definitions change.
 */
const NAV: Array<{ to: string; label: string; icon: typeof Terminal; permission?: Permission }> = [
  { to: '/console', label: 'Console', icon: Terminal, permission: 'query:run' },
  { to: '/dashboards', label: 'Dashboards', icon: LayoutDashboard, permission: 'query:run' },
  { to: '/history', label: 'History', icon: Clock, permission: 'catalog:read' },
  { to: '/audit', label: 'Audit', icon: FileSearch, permission: 'audit:read' },
  { to: '/admin', label: 'Admin', icon: Settings, permission: 'user:read' },
];

export function AppShell({ user, children }: { user: SessionUser; children: ReactNode }) {
  const theme = useConsoleStore((s) => s.theme);
  const setTheme = useConsoleStore((s) => s.setTheme);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const logout = useMutation({
    mutationFn: api.auth.logout,
    onSuccess: () => {
      // Clear the cache, not just the session: leaving fetched result data in
      // memory after sign-out on a shared machine defeats the purpose.
      queryClient.clear();
      navigate('/console');
      window.location.reload();
    },
    onError: () => {
      // The session is gone server-side either way in almost every failure
      // mode; reloading lands on the sign-in gate, which is where they wanted
      // to be. Leaving them staring at a spinner would be worse.
      window.location.reload();
    },
  });

  return (
    <div className="flex h-full flex-col bg-surface">
      <header className="flex h-11 shrink-0 items-center gap-1 border-b border-edge bg-surface-raised px-3">
        {/* The wordmark carries the brand; "Data Access" names this tool
            within it. Separated by a hairline rather than a slash so the two
            read as product-then-module, not as a path. */}
        <div className="mr-4 flex items-center gap-2.5">
          <IntervalLogo height={20} />
          <span className="h-4 w-px bg-edge-strong" aria-hidden="true" />
          <span className="text-xs font-semibold tracking-tight text-ink">Data Access</span>
        </div>

        <nav className="flex items-center gap-0.5">
          {NAV.filter(
            ({ permission }) => !permission || user.permissions.includes(permission),
          ).map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
                  isActive
                    ? 'bg-accent/10 text-accent'
                    : 'text-ink-muted hover:bg-surface-sunken hover:text-ink',
                )
              }
            >
              <Icon className="h-3.5 w-3.5" strokeWidth={2} />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="flex-1" />

        <SessionCountdown expiresAt={user.sessionExpiresAt} />

        <button
          className="btn btn-ghost px-1.5"
          onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
          title={theme === 'dark' ? 'Switch to light' : 'Switch to dark'}
          aria-label="Toggle colour theme"
        >
          {theme === 'dark' ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
        </button>

        <div className="ml-1 flex items-center gap-2.5 border-l border-edge pl-3">
          <div className="hidden text-right leading-tight sm:block">
            <div className="text-2xs font-medium text-ink">{user.name}</div>
            <div className="text-2xs text-ink-faint">{user.role.replace('_', ' ').toLowerCase()}</div>
          </div>
          <Avatar name={user.name} email={user.email} size={26} />
          <button
            className="btn btn-ghost px-1.5"
            onClick={() => logout.mutate()}
            disabled={logout.isPending}
            title="Sign out"
            aria-label="Sign out"
          >
            <LogOut className="h-3.5 w-3.5" />
          </button>
        </div>
      </header>

      <main className="min-h-0 flex-1">{children}</main>

      {/*
        Sign-out ends with a full page reload, which on a slow connection left
        several seconds of nothing happening after the click. The overlay goes
        up immediately so the action reads as taken, not ignored.
      */}
      {logout.isPending && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-surface/95 backdrop-blur-sm animate-fade-in">
          <IntervalLoader label="Signing you out…" />
        </div>
      )}

      <ToastViewport />
    </div>
  );
}

/**
 * Time left in the session.
 *
 * Sessions here are deliberately short. Surfacing the countdown means nobody
 * loses a half-written query to a silent expiry — it turns a security control
 * from something that happens *to* people into something they can see coming.
 */
function SessionCountdown({ expiresAt }: { expiresAt: string }) {
  const [remaining, setRemaining] = useState(() => msUntil(expiresAt));

  useEffect(() => {
    const timer = setInterval(() => setRemaining(msUntil(expiresAt)), 30_000);
    return () => clearInterval(timer);
  }, [expiresAt]);

  const minutes = Math.max(0, Math.floor(remaining / 60_000));
  // Silent until it matters — a permanent timer is just clutter.
  if (minutes > 30) return null;

  return (
    <span
      className={clsx(
        'chip mr-2',
        minutes <= 5
          ? 'border-danger/30 bg-danger/10 text-danger'
          : 'border-warn/30 bg-warn/10 text-warn',
      )}
      title="Your session ends then. Sign in again from the portal to continue."
    >
      <Clock className="h-3 w-3" />
      {minutes === 0 ? 'expiring' : `${minutes}m left`}
    </span>
  );
}

function msUntil(iso: string): number {
  return new Date(iso).getTime() - Date.now();
}
